import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from "axios";
import toast from "react-hot-toast";

interface ErrorResponse {
    detail?: string;
    message?: string;
}

/**
 * Chat Support Axios Instance
 * 
 * This is a separate axios instance specifically for chat support operations.
 * It has its own interceptors that are tailored for chat support needs:
 * 
 * Features:
 * 1. Custom authorization header for chat support API
 * 2. Specialized error handling for chat operations
 * 3. No token refresh logic (chat support uses different auth)
 * 4. Custom timeout settings for chat operations
 * 5. Enhanced logging for chat support debugging
 */
const chatBotUrl = process.env.REACT_APP_CHAT_BOT_BASE_URL || "";

const ChatAxios: AxiosInstance = axios.create({
    baseURL: chatBotUrl || "",
    timeout: 60000, // Longer timeout for chat operations (60 seconds)
    headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
    }
});

// Request interceptor for chat support
ChatAxios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // Add chat support specific authorization
        // This is a static token for chat support - different from main app auth
        const chatAuthToken = process.env.REACT_APP_CHAT_AUTH_TOKEN || "";
        
        if (config.headers) {
            config.headers['Authorization'] = `Bearer ${chatAuthToken}`;
        }

        // Log chat support requests for debugging
        console.log('🔍 ChatSupport Request:', {
            method: config.method?.toUpperCase(),
            url: config.url,
            baseURL: config.baseURL,
            headers: {
                Authorization: config.headers?.['Authorization'] ? 'Bearer [REDACTED]' : 'None',
                'Content-Type': config.headers?.['Content-Type']
            }
        });

        return config;
    },
    (error) => {
        console.error("❌ ChatSupport Request Error:", error);
        return Promise.reject(error);
    }
);

// Response interceptor for chat support
ChatAxios.interceptors.response.use(
    (response: AxiosResponse) => {
        // Log successful chat support responses
        console.log('✅ ChatSupport Response:', {
            status: response.status,
            statusText: response.statusText,
            url: response.config.url,
            method: response.config.method?.toUpperCase()
        });

        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig;
        const status = error?.response?.status;

        // Log chat support errors
        console.error('❌ ChatSupport Error:', {
            status,
            statusText: error?.response?.statusText,
            url: originalRequest?.url,
            method: originalRequest?.method?.toUpperCase(),
            message: error.message,
            responseData: error?.response?.data
        });

        // Handle different error scenarios for chat support
        if (status) {
            switch (status) {
                case 401:
                    // Chat support authentication failed
                    const authError = "Chat support authentication failed. Please refresh the page.";
                    console.error("🚪 ChatSupport 401 Unauthorized");
                    toast.error(authError);
                    break;

                case 403:
                    // Chat support access denied
                    const accessError = "Access denied to chat support. Please contact administrator.";
                    console.error("🚫 ChatSupport 403 Forbidden");
                    toast.error(accessError);
                    break;

                case 404:
                    // Chat support endpoint not found
                    const notFoundError = "Chat support service not available. Please try again later.";
                    console.error("🔍 ChatSupport 404 Not Found");
                    toast.error(notFoundError);
                    break;

                case 429:
                    // Rate limiting for chat support
                    const rateLimitError = "Too many chat requests. Please wait a moment and try again.";
                    console.error("⏱️ ChatSupport 429 Rate Limited");
                    toast.error(rateLimitError);
                    break;

                case 500:
                case 502:
                case 503:
                case 504:
                    // Server errors for chat support
                    const serverError = "Chat support service is temporarily unavailable. Please try again later.";
                    console.error(`🔥 ChatSupport ${status} Server Error`);
                    toast.error(serverError);
                    break;

                default:
                    // Handle other status codes
                    if (error.response?.data && typeof error.response.data === 'object') {
                        const responseData = error.response.data as ErrorResponse;
                        const errorMessage = responseData.detail || responseData.message || `Chat support error (${status})`;
                        console.error(`❌ ChatSupport ${status} Error:`, errorMessage);
                        toast.error(errorMessage);
                    } else {
                        const fallbackMessage = `Chat support error (${status}). Please try again.`;
                        console.error(`❌ ChatSupport ${status} Error:`, fallbackMessage);
                        toast.error(fallbackMessage);
                    }
            }
        } else {
            // Handle network errors and other issues
            if (error.code === 'ERR_NETWORK') {
                const networkError = "Network error connecting to chat support. Please check your internet connection.";
                console.error("🌐 ChatSupport Network Error");
                toast.error(networkError);
            } else if (error.code === 'ECONNABORTED') {
                const timeoutError = "Chat support request timed out. Please try again.";
                console.error("⏰ ChatSupport Timeout Error");
                toast.error(timeoutError);
            } else {
                const unknownError = "An unexpected error occurred with chat support. Please try again.";
                console.error("❓ ChatSupport Unknown Error:", error.message);
                toast.error(unknownError);
            }
        }

        return Promise.reject(error);
    }
);

export default ChatAxios;

/**
 * Health check function specifically for chat support API
 */
export const checkChatSupportHealth = async (): Promise<boolean> => {
    try {
        console.log("🏥 Checking ChatSupport API health...");
        const response = await ChatAxios.get('/health', { 
            timeout: 10000 
        });
        console.log("✅ ChatSupport API health check successful:", response.status);
        return true;
    } catch (error: any) {
        console.error("❌ ChatSupport API health check failed:", {
            message: error.message,
            code: error.code,
            status: error?.response?.status,
            isNetworkError: !error.response && !error.request,
            isCorsError: error.message?.includes('CORS') || error.code === 'ERR_NETWORK'
        });
        return false;
    }
};
